import { TableData } from '@/components/Table/type';
import { useTable } from '@/hooks/useTable';
import { ResearchedProduct } from '@/types/MarketResearch';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import Table from '@/components/Table';
import DefaultColumn from '@/components/Table/components/DefaultColumn';
import TableRowActions from '@/components/Table/components/TableRowActions';
import { currencyFormat, numberFormat } from '@/utils/stringFormatter';
import { getTimeDate } from '@/utils/date';
import { Eye, Edit3, History } from 'lucide-react';
import { useBaseStore } from '@/store';
import { Store } from '@/store/type';

export type ReseachedProductTable = TableData<ResearchedProduct>;

const columnHelper = createColumnHelper<ResearchedProduct>();

const stateSelector = (state: Store) => ({
  profile: state.authSlice.state.profile,
});

interface props {
  dataResearchedProduct: ResearchedProduct[];
  handleViewImage: (key: string) => void;
  handleCorrectResearch?: (product: ResearchedProduct) => void;
  handleViewAuditHistory?: (product: ResearchedProduct) => void;
}

const TableProductMarketResearch = ({
  dataResearchedProduct,
  handleViewImage,
  handleCorrectResearch,
  handleViewAuditHistory,
}: props) => {
  const { profile } = useBaseStore(stateSelector);
  const { tableState, setTable } = useTable<ResearchedProduct>('');

  const memoData = useMemo(
    () => dataResearchedProduct ?? [],
    [dataResearchedProduct],
  );
  const memoColumns = useMemo(
    () => [
      columnHelper.accessor('DS_REVENDA', {
        id: 'DS_REVENDA',
        header: 'Revenda',
        size: 120,
      }),
      columnHelper.accessor('NM_USUARIO', {
        id: 'NM_USUARIO',
        header: 'Representante',
        size: 160,
      }),
      columnHelper.accessor('DS_NOME_FANTASIA', {
        id: 'DS_NOME_FANTASIA',
        header: 'PDV',
        size: 240,
      }),
      columnHelper.accessor('DS_PRODUTO', {
        id: 'DS_PRODUTO',
        header: 'Produto',
        size: 280,
      }),
      columnHelper.accessor(
        ({ VL_PRECO_COMPRA }) =>
          VL_PRECO_COMPRA
            ? currencyFormat(Number(VL_PRECO_COMPRA))
            : currencyFormat(0),
        {
          cell: (props) => (
            <p className="text-right w-full">{`${props.getValue() || ''}`}</p>
          ),
          sortingFn: (rowA, rowB, _columnId) => {
            const statusA = rowA.original[_columnId];
            const statusB = rowB.original[_columnId];
            return Number(statusA) < Number(statusB) ? 1 : -1;
          },
          id: 'VL_PRECO_COMPRA',
          header: 'Vl. Compra',
          size: 120,
        },
      ),
      columnHelper.accessor(
        ({ QTD_VOLUME_COMPRA }) =>
          QTD_VOLUME_COMPRA
            ? numberFormat(Number(QTD_VOLUME_COMPRA))
            : numberFormat(0),
        {
          cell: (props) => (
            <p className="text-right w-full">{`${props.getValue() || ''}`}</p>
          ),
          sortingFn: (rowA, rowB, _columnId) => {
            const statusA = rowA.original[_columnId];
            const statusB = rowB.original[_columnId];
            return Number(statusA) < Number(statusB) ? 1 : -1;
          },
          id: 'QTD_VOLUME_COMPRA',
          header: 'Qtd. Compra',
          size: 120,
        },
      ),
      columnHelper.accessor(
        ({ VL_PRECO_VENDA }) =>
          VL_PRECO_VENDA
            ? currencyFormat(Number(VL_PRECO_VENDA))
            : currencyFormat(0),
        {
          cell: (props) => (
            <p className="text-right w-full">{`${props.getValue() || ''}`}</p>
          ),
          sortingFn: (rowA, rowB, _columnId) => {
            const statusA = rowA.original[_columnId];
            const statusB = rowB.original[_columnId];
            return Number(statusA) < Number(statusB) ? 1 : -1;
          },
          id: 'VL_PRECO_VENDA',
          header: 'Vl. Venda',
          size: 120,
        },
      ),
      columnHelper.accessor('DD_STATUS', {
        id: 'DD_STATUS',
        header: 'Status Lançamento',
        size: 155,
      }),
      columnHelper.accessor(
        ({ DT_PUBLICACAO }) =>
          DT_PUBLICACAO ? getTimeDate(new Date(DT_PUBLICACAO)) : '',
        {
          id: 'DT_PUBLICACAO',
          header: 'Dt. Publicação',
          size: 130,
        },
      ),
      columnHelper.accessor(
        ({ CREATED_AT }) =>
          CREATED_AT ? getTimeDate(new Date(CREATED_AT)) : '',
        {
          id: 'CREATED_AT',
          header: 'Dt. Criação',
          size: 130,
        },
      ),
      columnHelper.accessor(
        ({ DD_PRODUTO_EXTRA }) => (DD_PRODUTO_EXTRA == 1 ? 'Sim' : 'Não'),
        {
          id: 'DD_PRODUTO_EXTRA',
          header: 'Extra pesquisa?',
          size: 140,
        },
      ),
      columnHelper.display({
        id: 'Ações',
        cell: ({ row }) => {
          const actions: any[] = [];

          if (row.original.DS_CHAVE_ARQUIVO) {
            actions.push({
              action: () => handleViewImage(row.original.DS_CHAVE_ARQUIVO),
              label: `Visualizar Imagem`,
              icon: <Eye size={14} />,
            });
          }

          // RN001: Correção de pesquisas publicadas (exclusivo para Administrador da Revenda)
          if (
            profile === 'ADMINISTRADOR_REVENDA' &&
            row.original.DD_STATUS === 'Publicado' &&
            handleCorrectResearch
          ) {
            actions.push({
              action: () => handleCorrectResearch(row.original),
              label: `Corrigir Pesquisa`,
              icon: <Edit3 size={14} />,
            });
          }

          // RN005: Histórico de auditoria (Administrador da Revenda e Administrador Central)
          if (
            (profile === 'ADMINISTRADOR_REVENDA' ||
              profile === 'ADMINISTRADOR') &&
            handleViewAuditHistory
          ) {
            actions.push({
              action: () => handleViewAuditHistory(row.original),
              label: `Histórico de Auditoria`,
              icon: <History size={14} />,
            });
          }

          if (actions.length === 0) return '';

          return <TableRowActions actions={actions} />;
        },
        size: 60,
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [profile, handleCorrectResearch, handleViewAuditHistory],
  );

  if (memoData.length === 0) return null;

  return (
    <div className="flex-1 overflow-hidden">
      <Table<ReseachedProductTable>
        data={memoData}
        columns={memoColumns}
        getRowId={(row) => row.SK_PRODUTOS_PESQUISADOS.toString()}
        getTableInstance={(table) => setTable(table)}
        tableState={tableState}
        defaultColumn={{
          cell: ({ getValue }) => (
            <DefaultColumn>{getValue() as string}</DefaultColumn>
          ),
        }}
      />
    </div>
  );
};

export default TableProductMarketResearch;
